// Calculo de rangos y resultados del certificado en pantalla (para ver al capturar si cada
// medicion cumple). GEMELO de erp_backend/Utils/cmms/certificados.utils.js: el backend
// vuelve a calcular al guardar y es el que vale. Si se cambia uno, cambiar el otro.

export const RESULTADOS = [
    { value: 1, label: "Cumple", corto: "✓", color: "#2e9e6b" },
    { value: 2, label: "No cumple", corto: "✗", color: "#d64545" },
    { value: 3, label: "No aplica", corto: "N/A", color: "#7a8794" },
];

export const TIPOS_COLUMNA = [
    { value: "medicion", label: "Medición", ayuda: "Valor establecido ± tolerancia contra el valor entregado (cumple / no cumple)." },
    { value: "okError", label: "Prueba Ok / Error", ayuda: "Prueba cualitativa: se marca Ok o Error." },
    { value: "texto", label: "Dato fijo", ayuda: "Texto que se escribe en la plantilla (ej. Load 500 Ω)." },
    { value: "captura", label: "Texto a capturar", ayuda: "Se escribe al certificar (ej. observaciones)." },
];

export const VIGENCIAS = {
    vigente: { label: "Vigente", color: "green" },
    porVencer: { label: "Por vencer", color: "gold" },
    vencido: { label: "Vencido", color: "red" },
};

const esNumero = (v) => v !== null && v !== undefined && v !== "" && !isNaN(Number(v));
const num = (v) => (esNumero(v) ? Number(v) : null);
const redondear = (v) => (v === null ? null : Math.round(v * 10000) / 10000);

export const rangoCelda = (columna, celda) => {
    if (!columna || columna.tipo !== "medicion" || !celda) return null;
    const min = num(celda.minimo);
    const max = num(celda.maximo);
    if (min !== null || max !== null) return { min, max };
    const est = num(celda.establecido);
    const tol = num(celda.tolerancia) ?? num(columna.tolerancia);
    if (est === null || tol === null) return null;
    const delta = columna.toleranciaTipo === "abs" ? Math.abs(tol) : Math.abs(est) * (Math.abs(tol) / 100);
    let superior = est + delta;
    const tope = num(columna.limiteMaximo);
    if (tope !== null) superior = Math.min(superior, tope);
    return { min: redondear(Math.max(est - delta, 0)), max: redondear(superior) };
};

// true / false / null de una celda capturada.
export const cumpleCelda = (columna, celda) => {
    if (columna?.tipo === "okError") return celda?.valorTexto === "Ok" ? true : celda?.valorTexto === "Error" ? false : null;
    const rango = rangoCelda(columna, celda);
    const v = num(celda?.entregado);
    if (!rango || v === null) return null;
    return !((rango.min !== null && v < rango.min) || (rango.max !== null && v > rango.max));
};

// Valores que faltan: caracteristicas sin "despues", mediciones sin entregado, Ok / Error sin respuesta.
export const pendientesCertificado = (secciones) => {
    let n = 0;
    for (const s of secciones || []) {
        for (const it of s.items || []) if (!it.despues) n++;
        for (const f of s.filas || []) {
            for (const [j, c] of (f.celdas || []).entries()) {
                const tipo = (s.columnas || [])[j]?.tipo;
                if (tipo === "medicion" && num(c.entregado) === null) n++;
                else if (tipo === "okError" && !c.valorTexto) n++;
            }
        }
    }
    return n;
};

// false = algo no cumple; true = completo y todo cumple; null = incompleto o sin rangos.
export const resultadoCertificado = (secciones) => {
    let evaluado = false;
    for (const s of secciones || []) {
        for (const it of s.items || []) {
            if (it.despues === 2) return false;
            if (it.despues === 1) evaluado = true;
        }
        for (const f of s.filas || []) {
            for (const [j, c] of (f.celdas || []).entries()) {
                const r = cumpleCelda((s.columnas || [])[j], c);
                if (r === false) return false;
                if (r === true) evaluado = true;
            }
        }
    }
    if (pendientesCertificado(secciones) > 0) return null;
    return evaluado ? true : null;
};

// Resultado final si el ingeniero lo otorga aunque algo no cumpla: solo vale si algo no cumple;
// sale como cumple al estar completo, o incompleto mientras falten valores. { cumple, otorgado }
export const resultadoOtorgado = (calculado, pendientes, pedido) => {
    const otorgado = !!pedido && calculado === false;
    return { cumple: otorgado ? (pendientes > 0 ? null : true) : calculado, otorgado };
};

export const dictamenSugerido = ({ cumple, pendientes, dictamenAprobado, dictamenNoAprobado }) =>
    (cumple === false ? dictamenNoAprobado : cumple === true || !pendientes ? dictamenAprobado : "") || "";

// Columnas que ocupa una tabla en el PDF (mediciones = 3). Mas de 13 no cabe en carta.
export const MAX_ANCHO_TABLA = 13;
export const anchoTabla = (columnas) => 1 + (columnas || []).reduce((a, c) => a + (c.tipo === "medicion" ? 3 : 1), 0);

// Etiqueta del resultado para listas: Cumple / Cumple con obs. (otorgado) / No cumple / Incompleto (n) / Sin rangos.
export const etiquetaResultado = (cumple, pendientes, otorgado) =>
    cumple === true
        ? { label: otorgado ? "Cumple con obs." : "Cumple", color: "green" }
        : cumple === false
        ? { label: "No cumple", color: "red" }
        : pendientes
        ? { label: `Incompleto (${pendientes})`, color: "orange" }
        : { label: "Sin rangos", color: "default" };

export const formatoNumero = (v) => (esNumero(v) ? String(Math.round(Number(v) * 1000) / 1000) : "");

// "± 10 %" / "± 5 unidades" de una columna (o de la celda si tiene su propia tolerancia).
export const textoTolerancia = (columna, celda) => {
    const tol = num(celda?.tolerancia) ?? num(columna?.tolerancia);
    if (tol === null) return "";
    return columna.toleranciaTipo === "abs" ? `± ${formatoNumero(tol)}${columna.unidad ? ` ${columna.unidad}` : ""}` : `± ${formatoNumero(tol)} %`;
};

export const textoRango = (columna, celda) => {
    const r = rangoCelda(columna, celda);
    if (!r) return "";
    if (r.min !== null && r.max !== null) return `${formatoNumero(r.min)} – ${formatoNumero(r.max)}`;
    return r.min !== null ? `≥ ${formatoNumero(r.min)}` : `≤ ${formatoNumero(r.max)}`;
};

// Fecha-dia "YYYY-MM-DD" + meses (como EDATE) -> "YYYY-MM-DD".
export const sumarMeses = (fecha, meses) => {
    if (!fecha) return null;
    const [a, m, d] = String(fecha).slice(0, 10).split("-").map(Number);
    const ultimo = new Date(Date.UTC(a, m - 1 + Number(meses) + 1, 0)).getUTCDate();
    const r = new Date(Date.UTC(a, m - 1 + Number(meses), Math.min(d, ultimo)));
    return r.toISOString().slice(0, 10);
};

// Plantilla vacia para el editor.
let contador = 0;
export const llave = () => `k${Date.now()}_${contador++}`;
export const celdaVacia = () => ({ texto: "", establecido: null, tolerancia: null, minimo: null, maximo: null });
export const columnaNueva = () => ({ key: llave(), nombre: "", tipo: "medicion", unidad: "", tolerancia: 10, toleranciaTipo: "pct", limiteMaximo: null });
export const seccionChecklist = () => ({ key: llave(), tipo: "checklist", titulo: "Características a inspeccionar", items: [{ key: llave(), grupo: "", texto: "" }], etiquetaFila: "", columnas: [], filas: [] });
export const seccionTabla = () => {
    const col = columnaNueva();
    return { key: llave(), tipo: "tabla", titulo: "", items: [], etiquetaFila: "Modo", columnas: [{ ...col, nombre: "Valor" }], filas: [{ key: llave(), etiqueta: "", celdas: [celdaVacia()] }] };
};
